import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { FilenameConventionService } from 'src/app/services/filename-conventions/filename-convention.service';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import { FilenameConventionPayload } from 'src/app/models/filename-conventions/filename-convention-payload';

export interface RangeFilenameConventionCreateDialogData {
  organizationId: string;
}

@Component({
  selector: 'app-range-filename-convention-create-dialog',
  templateUrl: './range-filename-convention-create-dialog.component.html',
  styleUrls: ['./range-filename-convention-create-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RangeFilenameConventionCreateDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly familyBadge = 'Range';

  readonly nameControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(1)],
  });

  submitting = false;
  rangePayload: FilenameConventionPayload = { mode: 'rangeBased', entries: [] };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RangeFilenameConventionCreateDialogData,
    private readonly dialogRef: MatDialogRef<
      RangeFilenameConventionCreateDialogComponent,
      FilenameConvention | undefined
    >,
    private readonly service: FilenameConventionService,
  ) {}

  get canSubmit(): boolean {
    if (this.submitting) return false;
    return this.nameControl.valid;
  }

  onRangeEditorSave(payload: FilenameConventionPayload): void {
    this.rangePayload = payload;
  }

  submit(): void {
    if (!this.canSubmit) return;
    this.submitting = true;
    this.service
      .create(this.data.organizationId, {
        name: this.nameControl.value.trim(),
        value: { payload: this.rangePayload },
        writeReason: { kind: 'initialCreate' },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: FilenameConvention) => this.dialogRef.close(result),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }
}
